import { usePage } from '@inertiajs/react'
import dayjs from 'dayjs'
import { i18n } from 'shared/I18n'
import { colors } from 'theme'

import { type GlobalPageProps } from 'helpers/interfaces'

import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

import { faFacebookF, faInstagram, faLinkedin, faTwitter } from '@fortawesome/free-brands-svg-icons'
import { type IconDefinition, faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const SocialButton = ({ href, ariaLabel, icon }: { href: string; ariaLabel: string; icon: IconDefinition }) => {
  // Magic numbers below used for padding and dimensions
  return (
    <Link href={href} aria-label={ariaLabel} sx={{ color: 'inherit' }}>
      <Box sx={{ border: '1px solid white', borderRadius: '100px', pl: '10px', pt: '10px', width: 42, height: 42 }}>
        <FontAwesomeIcon icon={icon} color="inherit" style={{ height: 20, width: 20 }} />
      </Box>
    </Link>
  )
}

const fallbackLinks = {
  termsUrl: '#',
  privacyCookiesUrl: '#',
  healthSafetyUrl: '#',
  helpUrl: '#',
  aboutUrl: '#',
}

export const PageFooter = () => {
  const { props } = usePage<GlobalPageProps>()
  const links = props.links ?? fallbackLinks

  return (
    <AppBar position="relative" variant="footer" component="footer" sx={{ mt: 4 }}>
      <Container maxWidth="xl" sx={{ px: '10px', py: 2 }}>
        <Typography sx={{ mb: 3 }}>
          <FontAwesomeIcon icon={faCircleInfo} color={colors.infoLight} style={{ marginRight: 8 }} />
          {i18n.t('footer.cookies_text', {
            link: (
              <Link variant="link2" href={`${links.privacyCookiesUrl}`}>
                {i18n.t('footer.privacy_cookies_mb10')}
              </Link>
            ),
          })}
        </Typography>

        <Box
          sx={(theme) => ({
            display: 'grid',
            gap: 2,
            // Organise in three columns, one row on desktop
            gridTemplateColumns: 'auto auto auto',
            gridTemplateRows: 'auto',
            // Organise in one columns, three rows on mobile
            [theme.breakpoints.down('md')]: {
              gridTemplateColumns: 'auto',
              gridTemplateRows: 'auto auto auto',
            },
          })}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-start',
              alignItems: 'center',
              // Order in the centre on mobile
              [theme.breakpoints.down('md')]: {
                flexWrap: 'wrap',
                justifyContent: 'center',
              },
            })}
          >
            <Link variant="link2" href={`${links.termsUrl}`}>
              {i18n.t('footer.terms')}
            </Link>
            <Link variant="link2" href={`${links.privacyCookiesUrl}`}>
              {i18n.t('footer.privacy_cookies_mb10')}
            </Link>
            <Link variant="link2" href={`${links.healthSafetyUrl}`}>
              {i18n.t('footer.health_safety')}
            </Link>
            <Link variant="link2" href={`${links.helpUrl}`}>
              {i18n.t('footer.help')}
            </Link>
            <Link variant="link2" href={`${links.aboutUrl}`}>
              {i18n.t('footer.about')}
            </Link>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
            <SocialButton
              icon={faFacebookF}
              href="https://www.facebook.com/oxfordmedicalsimulation/"
              ariaLabel="Visit our Facebook profile"
            />
            <SocialButton
              icon={faTwitter}
              href="https://twitter.com/vrmedicalsim"
              ariaLabel="Visit our Twitter profile"
            />
            <SocialButton
              icon={faLinkedin}
              href="https://www.linkedin.com/company/oxfordmedicalsimulation/"
              ariaLabel="Visit our LinkedIn profile"
            />
            <SocialButton
              icon={faInstagram}
              href="https://www.instagram.com/oxfordmedicalsimulation/"
              ariaLabel="Visit our Instagram profile"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Typography>{i18n.t('footer.copyright', { year: String(dayjs().year()) })}</Typography>
          </Box>
        </Box>
      </Container>
    </AppBar>
  )
}
